import React, { useState } from 'react';
import { GraduationCap, Users, MessageSquare, Globe, CheckCircle } from 'lucide-react';

interface CollegeFormData {
  collegeName: string;
  contactName: string;
  contactEmail: string;
  phoneNumber: string;
  preferredDates: string;
  topicsOfInterest: string;
}

export const GetInvolved = () => {
  const [collegeFormData, setCollegeFormData] = useState<CollegeFormData>({
    collegeName: '',
    contactName: '',
    contactEmail: '',
    phoneNumber: '',
    preferredDates: '',
    topicsOfInterest: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [collegeFormSubmitted, setCollegeFormSubmitted] = useState(false);

  const handleCollegeFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCollegeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCollegeFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const collegeFormUrl = 'https://docs.google.com/forms/d/e/1FAIpQLScQP7Ts2cCofO184A4YjkloeHov8AG3nKXp2almTRAypVdUPg/formResponse';
      
      const formBody = new URLSearchParams({
        'entry.48426080': collegeFormData.collegeName,
        'entry.1863213580': collegeFormData.contactName,
        'entry.586327882': collegeFormData.contactEmail,
        'entry.881184678': collegeFormData.phoneNumber,
        'entry.384900682': collegeFormData.preferredDates,
        'entry.1366958416': collegeFormData.topicsOfInterest
      });

      await fetch(collegeFormUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      setCollegeFormSubmitted(true);
      setCollegeFormData({
        collegeName: '',
        contactName: '',
        contactEmail: '',
        phoneNumber: '',
        preferredDates: '',
        topicsOfInterest: ''
      });
    } catch (error) {
      console.error('Error submitting college form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="for-colleges" className="py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 md:mb-8">
            <div className="inline-flex items-center px-3 md:px-4 py-2 md:py-2 bg-white rounded-full shadow-lg border font-bold mb-2 md:mb-3" style={{borderColor: '#C4A052', color: '#6B4423'}}>
              <img 
                src="/A2ZAssist%20logo.png" 
                alt="A2Z Assist Logo" 
                className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3 object-contain"
              />
              Join Our Community
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
              Get Involved with A2ZCampus™
            </h2>
            <p className="text-sm md:text-base max-w-4xl mx-auto px-2" style={{color: '#2C2420'}}>
              Ready to bring transformative workshops to your campus or join our vibrant student community? 
              Choose your path to innovation and growth with our proprietary learning tools.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* For Colleges */}
            <div className="bg-white rounded-3xl p-3 md:p-5 border-2 shadow-xl" style={{borderColor: '#6B4423'}}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center mb-3 md:mb-4">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-3xl flex items-center justify-center mb-3 sm:mb-0 sm:mr-4 shadow-lg flex-shrink-0" style={{backgroundColor: '#6B4423'}}>
                  <GraduationCap className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold" style={{color: '#2C2420'}}>For Colleges</h3>
                  <p className="font-semibold text-sm md:text-base" style={{color: '#C4A052'}}>Transform Student Outcomes</p>
                </div>
              </div>
              <p className="mb-3 md:mb-4 text-sm md:text-base leading-relaxed" style={{color: '#36454F'}}>
                Invite A2ZCampus™ to conduct workshops at your institution. We'll customize 
                the content using our proprietary tools based on your students' needs and academic focus, 
                ensuring maximum impact and engagement.
              </p>
              
              {collegeFormSubmitted ? (
                <div className="text-center py-3 md:py-4">
                  <CheckCircle className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-2 md:mb-3" style={{color: '#FFFFFF', backgroundColor: '#C4A052', borderRadius: '50%', padding: '8px'}} />
                  <h4 className="text-lg md:text-xl font-bold mb-2 md:mb-2" style={{color: '#2C2420'}}>Thank You!</h4>
                  <div className="space-y-1 md:space-y-1 mb-3 md:mb-4 text-xs md:text-sm" style={{color: '#6B4423'}}>
                    <p>We appreciate your interest in partnering with A2ZCampus™ for workshops at your college.</p>
                    <p>Our team will review your request and get back to you shortly with next steps.</p>
                    <p>If you have any urgent questions, email us at <a href="mailto:info@a2zassist.com" className="hover:underline" style={{color: '#7A9CB5'}}>info@a2zassist.com</a>.</p>
                    <p className="font-semibold" style={{color: '#C4A052'}}>Looking forward to collaborating! 🚀</p>
                  </div>
                  <button
                    onClick={() => setCollegeFormSubmitted(false)}
                    className="px-4 md:px-5 py-2 md:py-2 text-white rounded-lg hover:opacity-90 transition-colors font-semibold text-sm md:text-base"
                    style={{backgroundColor: '#6B4423'}}
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleCollegeFormSubmit} className="space-y-3 md:space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-3">
                    <input
                      type="text"
                      name="collegeName"
                      value={collegeFormData.collegeName}
                      onChange={handleCollegeFormChange}
                      placeholder="College Name *"
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                    />
                    <input
                      type="text"
                      name="contactName"
                      value={collegeFormData.contactName}
                      onChange={handleCollegeFormChange}
                      placeholder="Contact Name *"
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-3">
                    <input
                      type="email"
                      name="contactEmail"
                      value={collegeFormData.contactEmail}
                      onChange={handleCollegeFormChange}
                      placeholder="Contact Email *"
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                    />
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={collegeFormData.phoneNumber}
                      onChange={handleCollegeFormChange}
                      placeholder="Phone Number *"
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                    />
                  </div>
                  <input
                    type="text"
                    name="preferredDates"
                    value={collegeFormData.preferredDates}
                    onChange={handleCollegeFormChange}
                    placeholder="Preferred Dates (e.g., March 15-20, 2024) *"
                    required
                    className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                    style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                  />
                  <textarea
                    name="topicsOfInterest"
                    value={collegeFormData.topicsOfInterest}
                    onChange={handleCollegeFormChange}
                    placeholder="Topics of Interest (e.g., ClarityKit™, Startup Simulator™, Real World Readiness™, etc.) *"
                    rows={3}
                    required
                    className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent resize-none transition-all text-sm md:text-base"
                    style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                  ></textarea>
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full text-white px-4 md:px-5 py-2 md:py-3 rounded-xl font-semibold transition-all transform hover:scale-105 text-sm md:text-base ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'
                    }`}
                    style={{backgroundColor: '#C4A052'}}
                  >
                    {isSubmitting ? 'Submitting...' : 'Request A2ZCampus™ Workshop'}
                  </button>
                </form>
              )}
            </div>

            {/* For Students */}
            <div className="bg-white rounded-3xl p-3 md:p-5 border-2 shadow-xl" style={{borderColor: '#FFFFFF', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 2px #C4A052'}}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center mb-3 md:mb-4">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-3xl flex items-center justify-center mb-3 sm:mb-0 sm:mr-4 shadow-lg flex-shrink-0" style={{backgroundColor: '#FFFFFF', border: '2px solid #C4A052'}}>
                  <Users className="w-6 h-6 md:w-8 md:h-8" style={{color: '#C4A052'}} />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold" style={{color: '#2C2420'}}>For Students</h3>
                  <p className="font-semibold text-sm md:text-base" style={{color: '#C4A052'}}>Accelerate Your Career</p>
                </div>
              </div>
              <p className="mb-3 md:mb-4 text-sm md:text-base leading-relaxed" style={{color: '#36454F'}}>
                Join our student community for workshops, projects, mentorship, and 
                career guidance from industry professionals. Get access to our proprietary learning tools,
                exclusive resources and networking opportunities.
              </p>
              
              <div className="space-y-3 md:space-y-3">
                <button 
                  onClick={() => {
                    const registerSection = document.getElementById('register-section');
                    if (registerSection) {
                      registerSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="block w-full text-white px-4 md:px-5 py-2 md:py-3 rounded-xl font-semibold text-center transition-all transform hover:scale-105 hover:opacity-90 text-sm md:text-base"
                  style={{backgroundColor: '#C4A052'}}
                >
                  Join A2ZCampus™ Community
                </button>
                
                <div className="grid grid-cols-2 gap-2 md:gap-2">
                  <a
                    href="https://chat.whatsapp.com/K92aNPM8garFqT71gZcEOp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-2 md:px-3 py-2 md:py-3 bg-white border rounded-xl hover:bg-gray-50 transition-all transform hover:scale-105 text-xs md:text-sm"
                    style={{borderColor: '#7A9CB5'}}
                  >
                    <MessageSquare className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{color: '#7A9CB5'}} />
                    <span style={{color: '#6B4423'}}>WhatsApp</span>
                  </a>
                  <a
                    href="https://www.linkedin.com/showcase/a2zcampus"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-2 md:px-3 py-2 md:py-3 bg-white border rounded-xl hover:bg-gray-50 transition-all transform hover:scale-105 text-xs md:text-sm"
                    style={{borderColor: '#7A9CB5'}}
                  >
                    <Globe className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{color: '#7A9CB5'}} />
                    <span style={{color: '#6B4423'}}>LinkedIn</span>
                  </a>
                </div>

                {/* Benefits List */}
                <div className="bg-white rounded-xl p-2 md:p-3 border" style={{borderColor: '#FFFFFF', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 0 0 1px #C4A052'}}>
                  <h4 className="font-semibold mb-2 md:mb-2 text-xs md:text-sm" style={{color: '#2C2420'}}>What You'll Get:</h4>
                  <ul className="space-y-1 text-xs md:text-xs" style={{color: '#6B4423'}}>
                    <li className="flex items-center">
                      <CheckCircle className="w-3 h-3 md:w-3 md:h-3 mr-2 flex-shrink-0" style={{color: '#C4A052'}} />
                      <span>Access to ClarityKit™ workshops</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-3 h-3 md:w-3 md:h-3 mr-2 flex-shrink-0" style={{color: '#C4A052'}} />
                      <span>Startup Simulator™ experiences</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-3 h-3 md:w-3 md:h-3 mr-2 flex-shrink-0" style={{color: '#C4A052'}} />
                      <span>Real World Readiness™ training</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-3 h-3 md:w-3 md:h-3 mr-2 flex-shrink-0" style={{color: '#C4A052'}} />
                      <span>One-on-one mentorship opportunities</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-3 h-3 md:w-3 md:h-3 mr-2 flex-shrink-0" style={{color: '#C4A052'}} />
                      <span>Career guidance and job referrals</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};